import clsx from 'clsx';
import type React from 'react';

import type * as api from '@gitbook/api-client';
import type { EvaluateMergeRuleResult, EvaluateMergeRulesResult } from '@gitbook/app-merge-rules';
import { intlPluralizeString } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Badge,
    type BadgeKind,
    Button,
    type ButtonKind,
    type ButtonSize,
    Description,
    DialogRoot,
    DialogTrigger,
    Disclosure,
    DisclosureChevron,
    DisclosurePanel,
    DisclosureTrigger,
    Heading,
    type IconDefinition,
    LabeledContainer,
    LabeledContainerActionableTitle,
    LabeledContainerBody,
    LabeledContainerHeader,
    Link,
    List,
    ListItem,
    ListItemBody,
    ListItemDescription,
    ListItemIndicator,
    type ListRenderItemInfo,
    Popover,
    PopoverRoot,
    PopoverTrigger,
    useDialogTriggerState,
} from '@gitbook/spine-react';

import { MergeRulesDialog, useEvaluateMergeRules, useSpaceMergeRules } from '../MergeRules';

type ChangeRequestMergeRulesStatusProps = {
    space: api.Space;
    changeRequest: api.ChangeRequest;
    mergeRulesEvaluation: EvaluateMergeRulesResult;
};

/**
 * Button to open the merge rules status popover
 */
export function ChangeRequestMergeRulesStatusButton(props: ChangeRequestMergeRulesStatusProps) {
    return (
        <PopoverRoot>
            <PopoverTrigger>
                <Button
                    label="Merging is blocked"
                    tooltip={{
                        label: 'Merge is blocked by merge rules',
                        description: 'Click to review the merge rules that are blocking the merge',
                    }}
                    kind="secondary"
                    size="medium"
                    trailingIcon={Icon.MergeRulesBlock}
                    iconOnly
                />
            </PopoverTrigger>
            <Popover placement="bottom-end" className="w-96 p-4">
                <ChangeRequestMergeRulesStatusWithData {...props} />
            </Popover>
        </PopoverRoot>
    );
}

/**
 * Fetch and summarize the merge rules status.
 */
export function ChangeRequestMergeRulesStatus(
    props: Omit<ChangeRequestMergeRulesStatusProps, 'mergeRulesEvaluation'>
) {
    const { space, changeRequest } = props;
    const mergeRulesEvaluation = useEvaluateMergeRules({ space, changeRequest });

    return (
        <ChangeRequestMergeRulesStatusWithData
            {...props}
            mergeRulesEvaluation={mergeRulesEvaluation}
        />
    );
}

/**
 * Summarize the merge rules status.
 */
function ChangeRequestMergeRulesStatusWithData(props: ChangeRequestMergeRulesStatusProps) {
    const { space, mergeRulesEvaluation } = props;
    const mergeRules = useSpaceMergeRules(space);
    const status = getMergeRulesStatusDescription({ space, mergeRules, mergeRulesEvaluation });

    return (
        <LabeledContainer>
            <LabeledContainerHeader>
                <LabeledContainerActionableTitle>
                    <ChangeRequestMergeRulesListTitle space={space} status={status} />
                </LabeledContainerActionableTitle>
                <Description>{status.description}</Description>
            </LabeledContainerHeader>
            {mergeRulesEvaluation.rejectedRules.length > 0 ||
            mergeRulesEvaluation.passedRules.length > 0 ||
            mergeRulesEvaluation.skippedRules.length > 0 ? (
                <LabeledContainerBody>
                    <ChangeRequestMergeRulesStatusList
                        mergeRulesEvaluation={mergeRulesEvaluation}
                    />
                </LabeledContainerBody>
            ) : null}
        </LabeledContainer>
    );
}

export function ChangeRequestMergeRulesStatusList(props: {
    mergeRulesEvaluation: EvaluateMergeRulesResult;
    buttonProps?: {
        kind?: ButtonKind;
        size?: ButtonSize;
    };
}) {
    const { mergeRulesEvaluation, buttonProps } = props;

    return (
        <>
            {mergeRulesEvaluation.rejectedRules.length > 0 ? (
                <ChangeRequestMergeRulesList
                    title={`${mergeRulesEvaluation.rejectedRules.length} failing ${intlPluralizeString(
                        {
                            count: mergeRulesEvaluation.rejectedRules.length,
                            singular: 'rule',
                            plural: 'rules',
                        }
                    )}`}
                    status="rejected"
                    rules={mergeRulesEvaluation.rejectedRules}
                    defaultExpanded
                    buttonProps={buttonProps}
                />
            ) : null}
            {mergeRulesEvaluation.passedRules.length > 0 ? (
                <ChangeRequestMergeRulesList
                    title={`${mergeRulesEvaluation.passedRules.length} passing ${intlPluralizeString(
                        {
                            count: mergeRulesEvaluation.passedRules.length,
                            singular: 'rule',
                            plural: 'rules',
                        }
                    )}`}
                    status="passed"
                    rules={mergeRulesEvaluation.passedRules}
                    defaultExpanded={false}
                    buttonProps={buttonProps}
                />
            ) : null}
            {mergeRulesEvaluation.skippedRules.length > 0 ? (
                <ChangeRequestMergeRulesList
                    title={`${mergeRulesEvaluation.skippedRules.length} skipped ${intlPluralizeString(
                        {
                            count: mergeRulesEvaluation.skippedRules.length,
                            singular: 'rule',
                            plural: 'rules',
                        }
                    )}`}
                    status="skipped"
                    rules={mergeRulesEvaluation.skippedRules}
                    defaultExpanded={false}
                    buttonProps={buttonProps}
                />
            ) : null}
        </>
    );
}

export function ChangeRequestMergeRulesListTitle(props: {
    space: api.Space;
    status: MergeRulesStatusDescription;
}) {
    const { space, status } = props;
    const mergeRulesDialogTrigger = useDialogTriggerState();

    return (
        <div className="flex w-full items-center justify-between gap-2">
            <div className="flex items-center gap-2">
                <Badge
                    size="medium"
                    iconOnly
                    icon={status.icon}
                    kind={status.badgeKind}
                    className="p-1"
                />
                <Heading className={clsx('flex-1')}>{status.label}</Heading>
            </div>
            {space.permissions.admin ? (
                <>
                    <Button
                        label="Configure merge rules"
                        iconOnly
                        kind="blank"
                        leadingIcon={Icon.Settings}
                        onPress={mergeRulesDialogTrigger.open}
                    />

                    <DialogRoot {...mergeRulesDialogTrigger.rootProps}>
                        <MergeRulesDialog space={space} />
                    </DialogRoot>
                </>
            ) : null}
        </div>
    );
}

function ChangeRequestMergeRulesList(props: {
    title: string;
    status: 'rejected' | 'passed' | 'skipped';
    rules: EvaluateMergeRuleResult[];
    defaultExpanded?: boolean;
    buttonProps?: {
        kind?: ButtonKind;
        size?: ButtonSize;
    };
}) {
    const { title, status, rules, defaultExpanded, buttonProps } = props;

    return (
        <Disclosure defaultExpanded={defaultExpanded}>
            <div>
                <div className="-ml-1.5">
                    <DisclosureTrigger>
                        <Button
                            trailingIcon={<DisclosureChevron />}
                            kind="blank"
                            size="small"
                            label={title}
                            {...buttonProps}
                        />
                    </DisclosureTrigger>
                </div>
                <DisclosurePanel className="flex flex-col">
                    <List
                        className="mt-1"
                        items={rules}
                        renderItem={({ item }: ListRenderItemInfo<EvaluateMergeRuleResult>) => (
                            <ListItem compact className="bg-transparent">
                                <ListItemIndicator>
                                    <Badge
                                        size="xsmall"
                                        kind={
                                            status === 'rejected'
                                                ? 'danger'
                                                : status === 'skipped'
                                                  ? 'secondary'
                                                  : 'success'
                                        }
                                        iconOnly
                                        icon={
                                            status === 'rejected'
                                                ? Icon.X
                                                : status === 'skipped'
                                                  ? Icon.Minus
                                                  : Icon.Check
                                        }
                                    />
                                </ListItemIndicator>
                                <ListItemBody>
                                    <ListItemDescription
                                        className={status === 'skipped' ? 'opacity-60' : ''}
                                    >
                                        {item.message}
                                    </ListItemDescription>
                                </ListItemBody>
                            </ListItem>
                        )}
                    />
                </DisclosurePanel>
            </div>
        </Disclosure>
    );
}

type MergeRulesStatusDescription = {
    icon: IconDefinition;
    badgeKind: BadgeKind;
    label: string;
    labelColor?: string;
    description: React.ReactNode;
};

export function getMergeRulesStatusDescription({
    space,
    mergeRules,
    mergeRulesEvaluation,
}: {
    space: api.Space;
    mergeRules: api.MergeRulesStandaloneConfiguration;
    mergeRulesEvaluation: EvaluateMergeRulesResult;
}): MergeRulesStatusDescription {
    if (mergeRulesEvaluation.result === 'rejected') {
        return {
            icon: Icon.MergeRulesBlock,
            badgeKind: 'danger',
            label: 'Merging is blocked',
            labelColor: 'text-danger',
            description: 'Merging is blocked by merge rules.',
        };
    }

    if (mergeRules.type === 'none') {
        return {
            icon: Icon.MergeRulesAllow,
            badgeKind: 'secondary',
            label: 'Merging is allowed',
            description: (
                <>
                    This space doesn't have merge rules yet.{' '}
                    <DialogRoot>
                        <DialogTrigger>
                            <Link kind="inherit">Configure merge rules</Link>
                        </DialogTrigger>
                        <MergeRulesDialog space={space} />
                    </DialogRoot>{' '}
                    to manage merge behavior.
                </>
            ),
        };
    }

    return {
        icon: Icon.MergeRulesAllow,
        badgeKind: 'success',
        label: 'Merging is allowed',
        labelColor: 'text-success',
        description: 'Merge rules have passed.',
    };
}
